import React, { Fragment } from "react";
import {
  Box,
  Container,
  Typography,
  CardMedia,
  CardContent,
  Grid,
  Button,
  Paper,
  Chip,
} from "@mui/material";
import { Download, CalendarToday, Update } from "@mui/icons-material";
import SEO from "../../components/ceo/CEO";
import { useParams } from "react-router-dom";
import useFetchObject from "../../api/useFetchObject";
import ErrorPage from "../../components/error/Error";
import BlogDetailsSkeleton from "./loading/BlogDetailsSkeleton";
import { format } from 'date-fns';

import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  WhatsappIcon,
} from "react-share";

const BlogDetails = () => {
  const { id } = useParams();
  const { data, isLoading, isError, isSuccess, error, refetch } =
    useFetchObject(["blog-details"], "blogs", id);

  const blogUrl = `https://shallafg.org/news/details/${id}`;

  const handleFileOpen = (fileUrl) => {
    window.open(fileUrl, '_blank');
  };

  if (isError) return <ErrorPage errorMessage={error.message} refetch={refetch} />;
  if (isLoading) return <BlogDetailsSkeleton />;

  if (isSuccess) {
    return (
      <Fragment>
        <SEO
          title={data.title}
          description={data.description}
          url={blogUrl}
          image={data.image}
        />
        
        {/* Hero Section */}
        <Box
          sx={{
            position: 'relative',
            height: '60vh',
            width: '100%',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(11, 15, 25, 0.7)',
              zIndex: 1,
            },
          }}
        >
          <CardMedia
            component="img"
            image={data.image}
            alt={data.title}
            sx={{
              height: '100%',
              width: '100%',
              objectFit: 'cover',
            }}
          />
          <Container
            maxWidth="lg"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              textAlign: 'center',
              color: '#fff',
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
                fontWeight: 700,
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                mb: 3,
                color: '#A9C9EA',
              }}
            >
              {data.title}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Chip
                icon={<CalendarToday sx={{ color: '#A9C9EA' }} />}
                label={format(new Date(data.date), 'MMMM dd, yyyy')}
                sx={{ backgroundColor: 'rgba(169, 201, 234, 0.1)', color: '#fff' }}
              />
              {data.updated && (
                <Chip
                  icon={<Update sx={{ color: '#A9C9EA' }} />}
                  label={`Updated: ${format(new Date(data.updated), 'MMM dd, yyyy')}`}
                  sx={{ backgroundColor: 'rgba(169, 201, 234, 0.1)', color: '#fff' }}
                />
              )}
            </Box>
          </Container>
        </Box>

        {/* Content Section */}
        <Box
          sx={{
            backgroundColor: '#0B0F19',
            minHeight: '100vh',
            py: 8,
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={4}>
              {/* Main Content */}
              <Grid 
                item 
                xs={12} 
                md={8}
                sx={{
                  order: { xs: 1, md: 1 }
                }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    backgroundColor: '#1A2E55',
                    borderRadius: 2,
                    overflow: 'hidden',
                    mb: 4,
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#fff',
                        fontSize: '1.1rem',
                        lineHeight: 1.8,
                        letterSpacing: '0.3px',
                        textAlign: 'justify',
                      }}
                      dangerouslySetInnerHTML={{
                        __html: data.description
                      }}
                    />
                  </CardContent>
                </Paper>
              </Grid>

              {/* Sidebar */}
              <Grid 
                item 
                xs={12} 
                md={4}
                sx={{
                  order: { xs: 2, md: 2 }
                }}
              >
                {data.file && (
                  <Paper
                    elevation={3}
                    sx={{
                      backgroundColor: '#1A2E55',
                      borderRadius: 2,
                      p: 3,
                      mb: 4,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: '#A9C9EA', mb: 2 }}
                    >
                      View Document
                    </Typography>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => handleFileOpen(data.file)}
                      startIcon={<Download />}
                      sx={{
                        backgroundColor: '#A9C9EA',
                        color: '#152A49',
                        '&:hover': {
                          backgroundColor: '#8BB5E0',
                        },
                        py: 1.5,
                        fontWeight: 600,
                      }}
                    >
                      Open Document
                    </Button>
                    {data.file_name && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', color: '#fff', mt: 1, textAlign: 'center' }}
                      >
                        {data.file_name}
                        {data.file_size && ` (${(data.file_size / 1024 / 1024).toFixed(2)} MB)`}
                      </Typography>
                    )}
                  </Paper>
                )}

                <Paper
                  elevation={3}
                  sx={{
                    backgroundColor: '#1A2E55',
                    borderRadius: 2,
                    p: 3,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: '#A9C9EA', mb: 2 }}
                  >
                    Article Information
                  </Typography>
                  <Box sx={{ color: '#fff' }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Published:</strong> {format(new Date(data.created), 'MMMM dd, yyyy')}
                    </Typography>
                    {data.updated && (
                      <Typography variant="body2">
                        <strong>Last Updated:</strong> {format(new Date(data.updated), 'MMMM dd, yyyy')}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Social Share Section - Moved to bottom on mobile */}
              <Grid 
                item 
                xs={12}
                sx={{
                  order: { xs: 3, md: 3 }
                }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    backgroundColor: '#1A2E55',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: '#A9C9EA', mb: 2 }}
                  >
                    Share this article
                  </Typography>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      gap: 2,
                      flexWrap: 'wrap'
                    }}
                  >
                    <FacebookShareButton url={blogUrl} quote={data.title}>
                      <FacebookIcon size={40} round />
                    </FacebookShareButton>
                    <TwitterShareButton url={blogUrl} title={data.title}>
                      <TwitterIcon size={40} round />
                    </TwitterShareButton>
                    <LinkedinShareButton url={blogUrl} summary={data.title}>
                      <LinkedinIcon size={40} round />
                    </LinkedinShareButton>
                    <WhatsappShareButton url={blogUrl} title={data.title}>
                      <WhatsappIcon size={40} round />
                    </WhatsappShareButton>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Fragment>
    );
  }
};

export default BlogDetails;
